import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

const StaticPage = ({ title, eyebrow, children }) => (
  <main className="py-20" data-testid="static-page">
    <div className="max-w-3xl mx-auto px-6">
      <div className="text-center mb-12">
        {eyebrow && <div className="text-[11px] tracking-[0.4em] uppercase text-[var(--gold-deep)] mb-4">{eyebrow}</div>}
        <h1 className="font-display text-5xl lg:text-6xl">{title}</h1>
      </div>
      <div className="prose-luxera text-[15px] leading-[1.85] text-[var(--noir-soft)] space-y-5">
        {children}
      </div>
    </div>
  </main>
);

export const AboutPage = () => (
  <StaticPage title="Our Story" eyebrow="— About">
    <p className="text-xl font-serif text-[var(--noir)] mb-8">Two passionate souls. One vision: luxury jewelry that lives with you.</p>
    <p>LUXERA KaLiMa was born from a simple belief: every woman deserves elegant jewelry that doesn't compromise. Pieces that feel as precious on day one as they will in five years — water, time, and life proof.</p>
    <p>Founded by two friends who searched for years for jewelry that combined timeless design, durability for daily life, and safety for sensitive skin. When they couldn't find it, they created it.</p>
    <p>Today, LUXERA KaLiMa offers refined, waterproof, hypoallergenic jewelry plated in 18K gold — designed for the modern woman who lives fully and shines effortlessly.</p>
    <div className="grid grid-cols-3 gap-6 my-10 not-prose">
      {[["18K", "Gold Plated"], ["100%", "Waterproof"], ["180", "Days Warranty"]].map(([k, v]) => (
        <div key={k} className="text-center">
          <div className="font-display text-4xl text-[var(--gold-deep)]">{k}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--grey)] mt-1">{v}</div>
        </div>
      ))}
    </div>
  </StaticPage>
);

export const JewelryInfoPage = () => (
  <StaticPage title="Jewelry Care" eyebrow="— Materials & Care">
    <h2 className="font-display text-2xl mt-8">Materials</h2>
    <p>All LUXERA KaLiMa pieces are crafted from <strong>premium stainless steel</strong> with <strong>18K gold plating</strong>. Our materials are nickel-free, lead-free, cadmium-free, and REACH compliant — TÜV Rheinland tested for sensitive skin safety.</p>
    <h2 className="font-display text-2xl mt-8">Waterproof Guarantee</h2>
    <p>Wear your LUXERA pieces in the shower, at the beach, or while swimming. They are designed to resist water, sweat, sand and chlorine without losing their shine.</p>
    <h2 className="font-display text-2xl mt-8">Care Tips</h2>
    <ul className="space-y-2">
      <li>· Clean with a soft, dry cloth after wear</li>
      <li>· Avoid contact with perfumes and harsh chemicals when possible</li>
      <li>· Store separately in the LUXERA pouch to prevent scratches</li>
      <li>· Tarnish-free — no polishing required</li>
    </ul>
    <h2 className="font-display text-2xl mt-8">Sizing</h2>
    <p>Each product page includes detailed dimensions. Most of our bracelets feature adjustable closures for the perfect fit.</p>
  </StaticPage>
);

export const FAQPage = () => {
  const [open, setOpen] = useState(0);
  const faqs = [
    { q: "Is LUXERA jewelry really waterproof?", a: "Yes — 100%. You can shower, swim, sweat, and live without removing your jewelry." },
    { q: "Will it tarnish over time?", a: "No. Our 18K gold plating on stainless steel is tarnish-resistant, made to last." },
    { q: "Is it safe for sensitive skin?", a: "Absolutely. Hypoallergenic, nickel-free, lead and cadmium free, REACH compliant, TÜV Rheinland tested." },
    { q: "How long does shipping take?", a: "Free shipping in Germany. Delivery in 3-5 business days. Worldwide shipping available." },
    { q: "What is your return policy?", a: "30-day money-back guarantee. Items must be unworn in original condition." },
    { q: "Do you offer warranty?", a: "Yes — 180-day warranty against manufacturing defects on every piece." },
    { q: "What payment methods do you accept?", a: "All major cards, PayPal, Apple Pay, Google Pay, Klarna, Shop Pay, Bancontact, EPS, Maestro, Union Pay." },
    { q: "Can I track my order?", a: "Yes, you'll receive a tracking link by email as soon as your order ships." },
  ];
  return (
    <StaticPage title="Questions & Answers" eyebrow="— FAQ">
      <div className="divide-y divide-[var(--champagne)]/60 border-y border-[var(--champagne)]/60 not-prose mt-8">
        {faqs.map((f, i) => (
          <div key={i}>
            <button onClick={() => setOpen(open === i ? null : i)} className="w-full py-5 flex items-center justify-between text-left" data-testid={`faq-${i}`}>
              <span className="font-serif text-lg">{f.q}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${open === i ? "rotate-180" : ""}`} />
            </button>
            {open === i && <p className="pb-5 text-[15px] text-[var(--noir-soft)] leading-relaxed">{f.a}</p>}
          </div>
        ))}
      </div>
    </StaticPage>
  );
};

export const ShippingPage = () => (
  <StaticPage title="Shipping Policy" eyebrow="— Delivery">
    <p><strong>Free shipping in Germany</strong> on all orders. Worldwide shipping available.</p>
    <p>Orders are processed within 1-2 business days. Once shipped, delivery times are:</p>
    <ul className="space-y-2">
      <li>· Germany: 3-5 business days (free)</li>
      <li>· EU: 5-7 business days</li>
      <li>· International: 7-14 business days</li>
    </ul>
    <p>You'll receive a tracking number by email when your order ships.</p>
  </StaticPage>
);

export const RefundPage = () => (
  <StaticPage title="Refund Policy" eyebrow="— 30-Day Returns">
    <p>We offer a <strong>30-day money-back guarantee</strong>. If you're not 100% satisfied, return your unworn jewelry in its original packaging for a full refund.</p>
    <p>To start a return, contact support@luxerakalima.com with your order number. We'll provide instructions and a return label (for Germany).</p>
    <p>Refunds are processed within 5-7 business days after we receive the returned item.</p>
  </StaticPage>
);

export const PrivacyPage = () => (
  <StaticPage title="Privacy Policy" eyebrow="— Your Data">
    <p>LUXERA KaLiMa respects your privacy. We collect only the information necessary to process your orders and improve your shopping experience.</p>
    <p>Your data is never sold to third parties. We use industry-standard encryption to protect your payment information.</p>
    <p>For full details or to request deletion of your data, contact: privacy@luxerakalima.com</p>
  </StaticPage>
);

export const TermsPage = () => (
  <StaticPage title="Terms of Service" eyebrow="— Legal">
    <p>By using luxerakalima.com, you agree to these terms. All transactions are subject to product availability and our acceptance.</p>
    <p>Prices are listed in the chosen currency and include applicable taxes where required by law. Shipping fees, if any, are calculated at checkout.</p>
    <p>LUXERA KaLiMa reserves the right to modify these terms at any time.</p>
  </StaticPage>
);

export const LegalNoticePage = () => (
  <StaticPage title="Legal Notice" eyebrow="— Impressum">
    <p><strong>LUXERA KaLiMa</strong></p>
    <p>E-commerce business operating in the European Union.</p>
    <p>Contact: support@luxerakalima.com</p>
    <p>All product images and content © 2026 LUXERA KaLiMa. All rights reserved.</p>
  </StaticPage>
);
